import { motion } from "framer-motion";
import { Download, MapPin, ArrowRight } from "lucide-react";
import { resolveMediaUrl } from "@/lib/api";
import { useLang } from "@/i18n/LangContext";

const HERO_BG =
    "https://static.prod-images.emergentagent.com/jobs/359e1a58-6c2d-46ea-8fb3-26c1f78309e7/images/a95eb212e6a7157080e857c2b21f4e41c4efa8aa1a51c72aefa5eccad73105d9.png";

export default function Hero({ modInfo }) {
    const { t } = useLang();
    const goto = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const heroBg = resolveMediaUrl(modInfo?.hero_image_url) || HERO_BG;

    return (
        <section
            data-testid="hero-section"
            className="relative min-h-screen w-full overflow-hidden flex items-end"
        >
            <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${heroBg})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[color:var(--bg-base)]/60 via-[color:var(--bg-base)]/80 to-[color:var(--bg-base)]" />
            <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--bg-base)] via-transparent to-transparent opacity-80" />

            <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32 pt-40 w-full">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.6 }}
                    className="flex items-center gap-3 mb-6"
                >
                    <span className="h-px w-12 bg-[color:var(--primary)]" />
                    <span className="label-eyebrow">
                        {t("Modyfikacja · Euro Truck Simulator 2")}
                    </span>
                </motion.div>

                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.7 }}
                    className="font-display font-bold uppercase text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tighter text-white leading-[0.95] mb-6 max-w-5xl"
                    data-testid="hero-title"
                >
                    Project<br />
                    <span className="text-[color:var(--primary)]">Ruda Śląska</span>
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                    className="text-lg md:text-xl text-[color:var(--text-secondary)] max-w-2xl leading-relaxed mb-10"
                    data-testid="hero-subtitle"
                >
                    {t(modInfo?.description ||
                        "Mapa dodająca tereny miasta Rudy Śląskiej w skali 1:1 do gry ETS2.")}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.55, duration: 0.6 }}
                    className="flex flex-wrap gap-4 mb-16"
                >
                    <button
                        data-testid="hero-download-btn"
                        onClick={() => goto("pobierz")}
                        className="btn-primary"
                    >
                        <Download size={16} /> {t("Pobierz modyfikację")}
                    </button>
                    <button
                        data-testid="hero-map-btn"
                        onClick={() => goto("mapa")}
                        className="btn-ghost"
                    >
                        <MapPin size={16} /> {t("Zobacz mapę")}
                    </button>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8, duration: 0.8 }}
                    className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl border-t border-white/10 pt-8"
                >
                    {[
                        { k: "Skala", v: "1:1" },
                        { k: "Wersja", v: modInfo?.version || "0.9.0" },
                        { k: "Dzielnica", v: modInfo?.districts || "Ruda" },
                        { k: "Drogi", v: modInfo?.roads_km || "6 km" },
                    ].map((s) => (
                        <div key={s.k} data-testid={`hero-stat-${s.k.toLowerCase()}`}>
                            <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--text-muted)] mb-1">
                                {s.k}
                            </div>
                            <div className="font-display text-2xl md:text-3xl text-white">
                                {s.v}
                            </div>
                        </div>
                    ))}
                </motion.div>
            </div>

            <button
                data-testid="hero-scroll-indicator"
                onClick={() => goto("o-projekcie")}
                className="hidden md:flex absolute bottom-6 right-12 z-10 items-center gap-3 text-xs uppercase tracking-[0.3em] text-[color:var(--text-secondary)] hover:text-[color:var(--primary)] transition-colors"
            >
                Przewiń <ArrowRight size={14} className="rotate-90" />
            </button>
        </section>
    );
}
